import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import client from "@/api/client";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const res = await client.get("/auth/me");
      setUser(res.data);
      return res.data;
    } catch (e) {
      // Expected when unauthenticated; log for diagnostics but keep user null.
      if (e?.response?.status !== 401) {
        console.error("AuthContext.fetchMe failed:", e);
      }
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // Also skip on /qa-login (testing-only bypass route) to avoid a race condition
    // where this initial 401 response could overwrite the user state set by qaLogin().
    if (window.location.hash?.includes("session_id=") || window.location.pathname === "/qa-login") {
      setLoading(false);
      return;
    }
    fetchMe().finally(() => setLoading(false));
  }, [fetchMe]);

  const loginWithGoogle = useCallback((postLoginPath = "/dashboard") => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + postLoginPath;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const qaLogin = useCallback(async (role, email) => {
    const res = await client.post("/auth/test-login", { role, email });
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await client.post("/auth/logout");
    } catch (e) {
      console.error("AuthContext.logout request failed:", e);
    }
    setUser(null);
  }, []);

  const completeOnboarding = useCallback(async (payload) => {
    const res = await client.post("/auth/onboarding", payload);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      fetchMe,
      loginWithGoogle,
      qaLogin,
      logout,
      completeOnboarding,
      setUser,
    }),
    [user, loading, fetchMe, loginWithGoogle, qaLogin, logout, completeOnboarding],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
